import { Account } from '@/domain/usecases'
import { LDClient, LDFlagSet } from 'launchdarkly-react-client-sdk'
import type { FeatureFlagUserContext } from './feature-flag-types'
import { getAllFlags } from './feature-flags'

/**
 * Internal state for the feature flag client
 */
const state = {
  client: undefined as LDClient | undefined,
  flagsMap: undefined as LDFlagSet | undefined,
  isInitialized: false
}

/**
 * User context kind for LaunchDarkly
 */
const USER_CONTEXT_KIND = 'user'

/**
 * Build user context from account profile
 * @internal
 */
const buildUserContext = (accountProfile: Account.Model): FeatureFlagUserContext => ({
  kind: USER_CONTEXT_KIND,
  key: accountProfile.id,
  accountType: accountProfile.accountType?.name,
  onboardingCompleted: accountProfile.onboardingCompleted,
  passPlanId: accountProfile.passPlan?.id,
  language: accountProfile.preferenceLanguage,
  companyId: accountProfile.company.id,
  companyUniqueId: accountProfile.company.uniqueId,
  companyName: accountProfile.company.name
})

/**
 * Build default flags map
 * @internal
 */
const buildDefaultFlagsMap = (): LDFlagSet => {
  const defaultFlags: LDFlagSet = {}
  getAllFlags().forEach(flag => {
    defaultFlags[flag.name] = flag.defaultValue
  })
  return defaultFlags
}

/**
 * Feature flag client manager
 * Singleton instance managing LaunchDarkly client and flag state
 */
export const FeatureFlagClient = {
  /**
   * Checks if LaunchDarkly client has been initialized and flags are ready.
   * Note: LaunchDarkly is initialized with anonymous context in main.tsx,
   * so this should always be true after the app loads.
   */
  isReady(): boolean {
    return state.isInitialized && !!state.flagsMap
  },

  /**
   * Sync internal state with LaunchDarkly client
   * Called once when the app loads to sync with the already-initialized LD client
   *
   * @param ldClient - LaunchDarkly client from useLDClient()
   */
  syncWithClient(ldClient: LDClient | undefined): void {
    if (!ldClient || state.isInitialized) return

    state.client = ldClient
    state.flagsMap = ldClient.allFlags()
    state.isInitialized = true

    console.log('[FeatureFlagClient] ✅ Synced with LaunchDarkly client')
  },

  /**
   * Identify user in LaunchDarkly and load their feature flags
   * Called on login/authentication
   *
   * @param ldClient - LaunchDarkly client from useLDClient()
   * @param accountProfile - User account profile
   */
  async identifyUser(ldClient: LDClient | undefined, accountProfile: Account.Model): Promise<void> {
    if (!ldClient) return

    state.client = ldClient
    state.isInitialized = false

    try {
      const userContext = buildUserContext(accountProfile)

      state.flagsMap = await ldClient.identify(userContext).catch(error => {
        console.error('Error identifying LaunchDarkly client:', error)
        return buildDefaultFlagsMap()
      })

      // Wait for LaunchDarkly to be fully initialized
      await ldClient.waitForInitialization().catch(error => {
        console.warn('[FeatureFlagClient] waitForInitialization failed, continuing anyway:', error)
      })

      state.isInitialized = true

      ldClient.on('error', error => {
        console.error('Feature Flag Initialization error:', error)
      })
    } catch (error) {
      console.error('[FeatureFlagClient] Failed to identify user:', error)
      state.flagsMap = buildDefaultFlagsMap()
      state.isInitialized = true
    }
  },

  /**
   * Get the current flags map (readonly)
   * @internal - Use specific flag helpers instead
   */
  getFlagsMap(): Readonly<LDFlagSet | undefined> {
    return Object.freeze(state.flagsMap)
  },

  /**
   * Get the internal client reference
   * @internal
   */
  getClient(): LDClient | undefined {
    return state.client
  },

  /**
   * Get experiments data
   * Returns empty object for now (can be extended later)
   */
  getExperiments(): Record<string, unknown> {
    // Future implementation
    return {}
  },

  /**
   * Reset client state (for testing or logout)
   * @internal
   */
  reset(): void {
    state.client = undefined
    state.flagsMap = undefined
    state.isInitialized = false
  }
}
